import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { DataDisplayTable } from 'components/data-display';
import { DateToLocal } from 'components/dates';
import { FormDateInput, FormHelperText, FormRadio, FormTextArea } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { Tooltip } from 'components/tooltip';
import { toApiDate } from 'pages/employee-reviews/complete/employeeReviewCompleteHelpers';

const defaultObject = {};
const defaultArray = [];

// FormDateInput otherwise defaults to only 5 years ahead, which is too narrow for a
// review scheduled further out.
const latestSelectableDate = moment("2100-01-01").toDate();

// The Outcome step of the complete-review wizard: the review type's outcome level
// (when in use), the next review date - the scheduled date with an optional custom one on a
// scheduled review type, or a manager-set date on a review type without a schedule - and a
// free-text summary. A caller that knows better what the date field does can give its heading and
// helper text, and an earlier recorded date the picker must still accept. A caller that knows why a
// date may not be changed gives that reason, and the recorded date it passes as the custom date is
// then shown in a disabled date field that explains the reason on hover instead of as read-only text.
export function OutcomeStep(
    {
        pendingReview = defaultObject,
        reviewOptions = defaultObject,
        outcomeLevelNumber = null,
        outcomeSummary = "",
        customNextReviewDate = null,
        nextReviewDateHeadingText = null,
        nextReviewDateHelperText = null,
        earliestNextReviewDate = null,
        nextReviewDateDisabledReason = null,
        loading = false,
        saving = false,
        outcomeLevelValidationMessage = null,
        onOutcomeLevelChange,
        onOutcomeSummaryChange,
        onCustomNextReviewDateChange
    }
) {

    const outcomeLevels = pendingReview?.outcomeLevels ?? defaultObject;
    const scheduledNextReviewDate = pendingReview?.scheduledNextReviewDate;

    // A next review date in the past is meaningless, so today is the earliest selectable
    // date. A recorded date that has since passed must still be accepted as it stands, so it
    // becomes the earliest date while the days between it and today stay unselectable.
    // Evaluated per render so the bound does not go stale in a long-lived tab.
    const today = moment().startOf('day').toDate();
    let earliestSelectableDate = today;
    let dateModifiers = null;

    if (earliestNextReviewDate != null) {
        const recordedDate = moment(earliestNextReviewDate).startOf('day').toDate();

        if (recordedDate < today) {
            earliestSelectableDate = recordedDate;
            dateModifiers = { disabledDays: { after: recordedDate, before: today } };
        }
    }

    function getLevelColour(levelNumber) {
        const option = (reviewOptions?.outcomeLevels ?? defaultArray).find((level) => level.number === levelNumber);

        if (option?.colour == null) {
            return "";
        }

        // StatusBlock/the swatch expects the hex value without the leading hash.
        return option.colour.replace("#", "");
    }

    function buildLevelOptions() {
        return (outcomeLevels.levels ?? defaultArray).map((level) => {
            const colour = getLevelColour(level.levelNumber);
            return {
                value: level.levelNumber,
                label: (
                    <span className="form-radio-label-with-swatch">
                        <span
                            className="form-radio-swatch"
                            // The swatch shows the level's configured colour, so it is set per level.
                            style={colour ? { backgroundColor: `#${colour}` } : undefined}
                        />
                        {`Level ${level.levelNumber} - ${level.levelName}`}
                    </span>
                )
            };
        });
    }

    function onLevelChange(event) {
        onOutcomeLevelChange(Number(event.target.value));
    }

    function onCustomDateChange(item) {
        onCustomNextReviewDateChange(toApiDate(item));
    }

    // The date field's heading and helper text tell the manager what the field does to the
    // driver's next review. On a review type without a schedule the date is the next review
    // itself, and a blank date leaves none. A scheduleActive missing from the review is read as
    // scheduled. On a scheduled type satisfiesScheduledReview describes the effect on the
    // schedule rather than whether the review is due, so a review that leaves the scheduled one
    // owed says what a date does to it instead of offering a scheduled date to override.
    let customDateHeadingText = "Custom Review Date";
    let customDateHelperText = "Only set this if you don't want to use the scheduled review date.";

    if (pendingReview?.scheduleActive === false) {
        customDateHeadingText = "Next review date";
        customDateHelperText = `Set the date ${pendingReview.employee?.name}'s next review is due. Leave blank for no next review.`;
    } else if (pendingReview?.satisfiesScheduledReview === false && pendingReview?.pendingReviewId != null) {
        customDateHelperText = (<>Set a date to move {pendingReview.employee?.name}'s next review to it. Leave blank to leave the scheduled review due <DateToLocal format="DD/MM/YYYY">{pendingReview.dueDate}</DateToLocal> unchanged.</>);
    } else if (scheduledNextReviewDate != null) {
        customDateHelperText = (<>Only set this if you don't want to use the scheduled review date (<DateToLocal format="DD/MM/YYYY">{scheduledNextReviewDate}</DateToLocal>).</>);
    }

    if (nextReviewDateHeadingText != null) {
        customDateHeadingText = nextReviewDateHeadingText;
    }

    if (nextReviewDateHelperText != null) {
        customDateHelperText = nextReviewDateHelperText;
    }

    // A date that may not be changed is shown read-only as the scheduled date, or, when the caller
    // gives the reason, as the recorded date in a disabled date field explaining that reason on hover.
    // A disabled input fires no hover events of its own, so a wrapping element gives the tooltip its target.
    let fixedNextReviewDate = (
        <ShowHide
            evaluator={scheduledNextReviewDate != null}
            show={(
                <DataDisplayTable
                    loading={loading}
                    displayInRows={false}
                    data={[{
                        key: "next-review-date",
                        header: "Next review date",
                        value: <DateToLocal format="DD/MM/YYYY">{scheduledNextReviewDate}</DateToLocal>
                    }]}
                />
            )}
        />
    );

    if (nextReviewDateDisabledReason != null) {
        fixedNextReviewDate = (
            <ShowHide
                evaluator={customNextReviewDate != null}
                show={(
                    <Tooltip content={nextReviewDateDisabledReason} position="top">
                        <div>
                            <FormDateInput
                                id="outcome-custom-next-review-date"
                                headingText={customDateHeadingText}
                                value={customNextReviewDate}
                                minDate={earliestSelectableDate}
                                maxDate={latestSelectableDate}
                                loading={loading}
                                disabled
                                onChange={onCustomDateChange}
                            />
                        </div>
                    </Tooltip>
                )}
            />
        );
    }

    return (
        <div>
            <h2>Outcome</h2>
            <FormHelperText loading={loading}>Summarise the review and set the outcome.</FormHelperText>
            <ShowHide
                evaluator={outcomeLevels.active === true}
                show={(
                    <FormRadio
                        id="outcome-level"
                        headingText="Outcome level"
                        loading={loading}
                        disabled={saving}
                        bordered
                        options={buildLevelOptions()}
                        selectedValue={outcomeLevelNumber}
                        onChange={onLevelChange}
                        dangerHelperText={outcomeLevelValidationMessage}
                    />
                )}
            />
            <ShowHide
                evaluator={pendingReview?.allowCustomNextReviewDate === true}
                show={(
                    <FormDateInput
                        id="outcome-custom-next-review-date"
                        headingText={customDateHeadingText}
                        helperText={customDateHelperText}
                        value={customNextReviewDate}
                        minDate={earliestSelectableDate}
                        maxDate={latestSelectableDate}
                        modifiers={dateModifiers}
                        loading={loading}
                        disabled={saving}
                        onChange={onCustomDateChange}
                    />
                )}
                hide={fixedNextReviewDate}
            />
            <FormTextArea
                id="outcome-summary"
                headingText="Outcome Summary"
                placeholder="Summarise the outcome of this review..."
                value={outcomeSummary}
                loading={loading}
                disabled={saving}
                fill
                onChange={onOutcomeSummaryChange}
            />
        </div>
    );
}

OutcomeStep.propTypes = {
    pendingReview: PropTypes.object,
    reviewOptions: PropTypes.object,
    outcomeLevelNumber: PropTypes.number,
    outcomeSummary: PropTypes.string,
    customNextReviewDate: PropTypes.string,
    nextReviewDateHeadingText: PropTypes.string,
    nextReviewDateHelperText: PropTypes.node,
    earliestNextReviewDate: PropTypes.string,
    nextReviewDateDisabledReason: PropTypes.string,
    loading: PropTypes.bool,
    saving: PropTypes.bool,
    outcomeLevelValidationMessage: PropTypes.string,
    onOutcomeLevelChange: PropTypes.func.isRequired,
    onOutcomeSummaryChange: PropTypes.func.isRequired,
    onCustomNextReviewDateChange: PropTypes.func.isRequired
};
